import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { Dangerous, InfoOutlined, WarningAmber } from '@mui/icons-material';

import { ConfirmContext, type ConfirmApi, type ConfirmOptions } from './confirm.context';

/**
 * ConfirmProvider
 *
 * Centralized confirmation dialog service (Promise-based).
 * Example:
 *   const confirm = useConfirm();
 *   const ok = await confirm({ title: 'Delete', priority: 'danger' });
 *
 * Design goals:
 * - Keep pages free from "open state + dialog JSX"
 * - Provide consistent UX (copy, buttons, colors, keyboard focus)
 * - Support destructive confirmation flows safely
 */
export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const resolverRef = useRef<((value: boolean) => void) | null>(null);

  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmOptions>({
    title: '',
    priority: 'info',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
  });

  const confirm = useCallback<ConfirmApi>((nextOptions) => {
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;

      setOptions({
        priority: nextOptions.priority ?? 'info',
        confirmText: nextOptions.confirmText ?? 'Confirm',
        cancelText: nextOptions.cancelText ?? 'Cancel',
        autoFocusConfirm: nextOptions.autoFocusConfirm,
        title: nextOptions.title,
        description: nextOptions.description,
      });

      setOpen(true);
    });
  }, []);

  const closeWith = (result: boolean) => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    setOpen(false);

    // Resolve after closing to avoid state updates during render.
    const resolver = resolverRef.current;
    resolverRef.current = null;
    resolver?.(result);
  };

  const icon = useMemo(() => {
    switch (options.priority) {
      case 'danger':
        return <Dangerous sx={{ fontSize: 22, color: 'error.main', flexShrink: 0 }} />;
      case 'warning':
        return <WarningAmber sx={{ fontSize: 22, color: 'warning.main', flexShrink: 0 }} />;
      case 'info':
      default:
        return <InfoOutlined sx={{ fontSize: 22, color: 'info.main', flexShrink: 0 }} />;
    }
  }, [options.priority]);

  const titleColor =
    options.priority === 'danger'
      ? 'error.main'
      : options.priority === 'warning'
        ? 'warning.main'
        : 'info.main';

  // Safer defaults for destructive actions
  const autoFocusConfirm =
    options.autoFocusConfirm ??
    (options.priority === 'danger' ? false : true);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}

      <Dialog
        open={open}
        onClose={() => closeWith(false)}
        aria-labelledby="confirm-dialog-title"
        maxWidth="xs"
        fullWidth
        disableRestoreFocus
      >
        <DialogTitle id="confirm-dialog-title" sx={{ pb: 1.25 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            {icon}
            <Typography
              variant="h6"
              fontWeight={900}
              sx={{ color: titleColor, minWidth: 0, wordBreak: 'break-word' }}
            >
              {options.title}
            </Typography>
          </Stack>
        </DialogTitle>

        {options.description ? (
          <DialogContent sx={{ pt: 0 }}>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              {options.description}
            </Typography>
          </DialogContent>
        ) : null}

        <DialogActions sx={{ p: 2, pt: options.description ? 1.5 : 2 }}>
          <Button variant="outlined" onClick={() => closeWith(false)}>
            {options.cancelText}
          </Button>

          <Button
            variant="contained"
            color={options.priority === 'danger' ? 'error' : 'primary'}
            onClick={() => closeWith(true)}
            autoFocus={autoFocusConfirm}
          >
            {options.confirmText}
          </Button>
        </DialogActions>
      </Dialog>
    </ConfirmContext.Provider>
  );
}
